import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, CalendarCheck } from "lucide-react";
import { canonical, ROOFING_CONTRACTOR_ID } from "@/components/site/site-config";
import { PageHero, ServicePageBody } from "@/components/site/PageShell";
import { Breadcrumbs, breadcrumbListJsonLd } from "@/components/site/Breadcrumbs";
import { BUSINESS } from "@/components/site/constants";

const CRUMBS = [
  { label: "Service Areas", href: "/service-areas" },
  { label: "Citrus Hills, FL", href: "/roofing-company-citrus-hills-fl" },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "Do you charge for a roof inspection?",
    a: "No. Destination Roofing provides a free roof inspection and written estimate. Deston Judy, the owner, generally performs inspections personally, so the person looking at the roof is the person who can answer your questions about it.",
  },
  {
    q: "My roof only leaks in one spot. Does that mean I need a whole new roof?",
    a: "Not necessarily. A single leak around a pipe boot, a valley, a chimney, or a skylight can often be repaired when the rest of the roofing system is still in serviceable condition. Replacement becomes the more practical option when the covering is broadly worn, leaks keep returning in new places, or the deck underneath has been affected.",
  },
  {
    q: "Is a larger roof harder to replace?",
    a: "A larger roof usually means more surface area, more detail work, and more material, so the project takes longer and costs more than a small simple roof. The added complexity is usually in the details — valleys, hips, transitions, chimneys, skylights, and flashings — rather than in the open field of the roof.",
  },
  {
    q: "Should I choose shingles or metal?",
    a: "Both are legitimate choices in Florida. Architectural shingles are the most common residential system and are generally less expensive up front. Metal roofing costs more initially and typically lasts longer. The right answer depends on the roof structure, slope, detail work, budget, and how long you plan to own the home.",
  },
  {
    q: "Can you work with multiple roof sections or attached structures?",
    a: "Yes. Attached garages, lanais, porches, and additions are common, and they are often built at a different slope than the main house. Those transitions need to be detailed correctly, and low-slope sections may need a different roofing system than the main roof.",
  },
  {
    q: "Do you handle gutters and soffit too?",
    a: "Yes. Destination Roofing installs and repairs seamless gutters as well as soffit and fascia, which is useful when a roof replacement exposes wood damage or when drainage is part of the problem.",
  },
  {
    q: "Are you licensed and insured?",
    a: `Yes. Destination Roofing Inc. is a licensed and insured Florida roofing contractor, License #${BUSINESS.licenseNumber}.`,
  },
];

function TrustRow() {
  const items = [
    "Free Roof Inspection & Estimate",
    "Owner-performed inspections",
    "Family-owned roofing contractor",
    "Licensed and insured",
    `License #${BUSINESS.licenseNumber}`,
    "Repair and full replacement options",
  ];
  return (
    <div className="not-prose bg-muted/50 border border-border rounded-lg p-4 my-6">
      <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {items.map((t) => (
          <li key={t} className="flex items-start gap-2">
            <span className="mt-1 size-1.5 rounded-full bg-cta shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export const Route = createFileRoute("/roofing-company-citrus-hills-fl")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Citrus Hills, FL | Destination Roofing" },
      {
        name: "description",
        content:
          "Family-owned roofing company serving Citrus Hills, FL. Roof repair, replacement, metal and shingle roofing, free inspections. Licensed & insured. (352) 901-3094.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Roofing Company in Citrus Hills, FL | Destination Roofing" },
      {
        property: "og:description",
        content:
          "Roof repair, roof replacement, metal and shingle roofing for Citrus Hills homeowners. Free roof inspection and written estimate.",
      },
      { property: "og:url", content: canonical("/roofing-company-citrus-hills-fl") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: canonical("/roofing-company-citrus-hills-fl") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify([
          breadcrumbListJsonLd(CRUMBS),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Roofing services in Citrus Hills, FL",
            serviceType: "Roofing Contractor",
            description:
              "Roof repair, roof replacement, metal roofing, shingle roofing, roof inspections, gutters and soffit for homeowners in Citrus Hills, Florida.",
            url: canonical("/roofing-company-citrus-hills-fl"),
            provider: { "@id": ROOFING_CONTRACTOR_ID },
            areaServed: { "@type": "Place", name: "Citrus Hills, Florida" },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={CRUMBS} />
      <PageHero
        eyebrow="Local Roofing"
        title="Roofing Company in Citrus Hills, FL"
        subtitle="Destination Roofing Inc. is a family-owned, licensed and insured Citrus County roofing contractor. Free Roof Inspection & Estimate — including on larger homes with more complex roof designs."
      />
      <ServicePageBody title="Your Free Inspection" service="Roofing in Citrus Hills, FL">
        <p>
          Destination Roofing Inc. is a family-owned roofing contractor working throughout Citrus County, and Citrus Hills is part of our regular service area. We are a service-area business rather than a storefront, so the crew comes to the property, the owner looks at the roof, and you get a written estimate before anything is scheduled.
        </p>
        <TrustRow />

        <h2>Roofing Services Available in the Community</h2>
        <p>
          Homeowners here call us for the full range of residential roofing work, plus the exterior components that keep water moving off the house:
        </p>
        <ul>
          <li><Link to="/roof-replacement">Roof replacement</Link> — full tear-off and new roof installation</li>
          <li><Link to="/roof-repair">Roof repair</Link> — leaks, wind damage, missing shingles, failed flashings</li>
          <li><Link to="/shingle-roofing">Shingle roofing</Link> — architectural asphalt shingle systems</li>
          <li><Link to="/metal-roofing">Metal roofing</Link> — exposed-fastener and standing-seam systems</li>
          <li><Link to="/flat-roofing">Flat and low-slope roofing</Link> — lanais, additions and porch roofs</li>
          <li><Link to="/roof-inspection">Roof inspections</Link> with photographs and a written estimate</li>
          <li><Link to="/storm-damage-roof-repair">Storm-damage roof repair</Link></li>
          <li><Link to="/gutters">Seamless gutters</Link> and <Link to="/soffit">soffit and fascia</Link></li>
          <li><Link to="/third-nail-wind-mitigation">Third-nail wind mitigation</Link></li>
          <li><Link to="/commercial-roofing">Commercial roofing</Link></li>
        </ul>

        <h2>When a Roof Is Larger or More Complex</h2>
        <p>
          Not every home in the area has a complicated roof, but a good share of the housing stock here was built with more architectural detail than a simple gable house — and that changes how a roof should be evaluated. The more shapes a roof has, the more places water has to be directed correctly.
        </p>
        <h3>More valleys, hips, ridges and transitions</h3>
        <p>
          Valleys concentrate water from two roof planes into one path. Hips and ridges need proper closure and ventilation. Transitions where a roof meets a wall, a second story, or an attached structure depend entirely on flashing rather than on the covering itself. These are the places we look at first during an inspection, because they fail long before the open field of a roof does.
        </p>
        <h3>Larger surface areas and multiple roof sections</h3>
        <p>
          A larger roof means more material, more staging, and more time on site — but it also means a leak can travel a long way before it shows up on a ceiling. Homes with several separate roof sections sometimes have sections of different ages, and one section may need work while another has years of service left. We price what actually needs to be done rather than assuming the whole roof goes at once.
        </p>
        <h3>Chimneys and skylights</h3>
        <p>
          Chimneys and skylights are penetrations, and penetrations are where roofs leak. Counterflashing, step flashing, curbs, and seals all age. During a roof replacement, these details are the right time to correct rather than reuse.
        </p>
        <h3>Ventilation</h3>
        <p>
          Attic ventilation has to balance intake at the soffit with exhaust near the ridge. On a roof with several separate sections, each section effectively has its own ventilation story. Poor airflow shows up as heat buildup and moisture in the attic long before it shows up as a visible roof problem.
        </p>
        <h3>Gutters and drainage</h3>
        <p>
          A larger roof sheds a lot of water in a Florida downpour. Where that water lands matters — for the fascia, the soffit, and the ground around the house. Seamless gutters sized and placed correctly are part of the roofing system, not an afterthought.
        </p>

        <h2>Roof Repair or Roof Replacement?</h2>
        <div className="not-prose grid md:grid-cols-2 gap-4 my-6">
          <div className="border-2 border-cta rounded-lg p-5 bg-card">
            <h3 className="font-display font-bold text-lg mb-3">Repair may make sense when:</h3>
            <ul className="space-y-1.5 text-sm">
              <li>· The covering is still in serviceable condition overall</li>
              <li>· The problem traces to one detail — a boot, valley, skylight or flashing</li>
              <li>· Damage is limited to one area or one roof section</li>
              <li>· The decking underneath is still sound</li>
            </ul>
          </div>
          <div className="border border-border rounded-lg p-5 bg-card">
            <h3 className="font-display font-bold text-lg mb-3">Replacement may make more sense when:</h3>
            <ul className="space-y-1.5 text-sm">
              <li>· Wear is widespread rather than isolated</li>
              <li>· Leaks keep returning in different places after repairs</li>
              <li>· Flashings and details are failing in multiple areas</li>
              <li>· The roof is near the end of its useful life</li>
            </ul>
          </div>
        </div>
        <p>
          You should not have to guess which one you need. A free <Link to="/roof-inspection">roof inspection</Link> gives you photos of what is actually happening up there, an explanation in plain language, and a written estimate you can compare.
        </p>

        <h2>Shingle Roofing</h2>
        <p>
          Architectural asphalt shingles remain the most common residential roofing system in Florida, and Destination Roofing primarily installs Atlas shingle systems. On a roof with a lot of detail work, the value of a shingle system is that it adapts well to complex shapes — valleys, hips, and dormers can all be detailed in the same material.
        </p>
        <p>
          What matters most on a shingle replacement is what happens under and around the shingles: the condition of the decking, the underlayment, the valley detail, the flashing at walls and penetrations, and the fastening. You can read more on our <Link to="/shingle-roofing">shingle roofing page</Link>, including the details we hold our own crew to.
        </p>

        <h2>Metal Roofing</h2>
        <p>
          Metal is a longer-term system, and it is a common choice for homeowners who plan to stay in the house. Destination Roofing installs both exposed-fastener metal roofing and standing-seam metal roofing.
        </p>
        <ul>
          <li><strong>Exposed-fastener panels</strong> — screws go through the panel face into the structure. A proven, more affordable metal system; the fasteners and sealants need periodic inspection as the roof ages.</li>
          <li><strong>Standing seam</strong> — the primary panel fasteners are concealed beneath the seams rather than exposed to weather, which is part of why it costs more.</li>
        </ul>
        <p>
          On a roof with many planes and penetrations, metal panel layout takes more planning, and details around chimneys and skylights matter more. That is a design conversation, not a sales pitch — see <Link to="/metal-roofing">metal roofing</Link> for how we approach both systems.
        </p>

        <h2>Florida Weather and What It Does to a Roof</h2>
        <p>
          Roofs in this part of Florida deal with intense summer sun, heavy rain that arrives fast, high humidity, and wind during storm season. Heat and UV age roofing materials from above. Driving rain finds weak flashings that a gentle rain never would. Humidity and poor attic airflow work on the roof from underneath. Wooded lots add another factor — debris sitting in valleys holds moisture against the roof.
        </p>
        <p>
          Practical maintenance goes a long way: keep valleys and gutters clear, look at the roof after a major storm, and have anything questionable checked before it becomes an interior problem. If a storm has already come through, our <Link to="/storm-damage-roof-repair">storm-damage roof repair</Link> page explains what we look for.
        </p>

        <h2>Wind Mitigation</h2>
        <p>
          When a roof is replaced, there may be opportunities to improve how the roof structure is fastened, including third-nail wind mitigation. Eligible improvements may help a homeowner qualify for insurance credits — the insurance company determines the actual outcome, not the roofer. It is worth asking about during your inspection, especially on a larger roof, since credits are typically tied to the insured value. See <Link to="/third-nail-wind-mitigation">third-nail wind mitigation</Link> for details.
        </p>

        <h2>Working With a Local Family-Owned Roofer</h2>
        <p>
          Destination Roofing is family-owned, and owner Deston Judy generally performs roof inspections personally and stays involved with projects. On a larger or more complicated roof, that matters: the person who assessed the valleys, the flashings, and the ventilation is the same person answering your questions while the work is underway.
        </p>
        <p>
          We will tell you honestly whether a repair will hold or whether replacement is the better use of your money. If a repair is the right call, we will say so. You can see the communities we cover on our <Link to="/service-areas">service areas</Link> page.
        </p>

        <h2>Long-Term Ownership Considerations</h2>
        <p>
          How long you plan to stay in the home is a legitimate part of the decision. If this is a long-term home, the higher up-front cost of a metal system may be worth it. If the plan is shorter, a quality shingle replacement with correct details may serve you better. Either way, the roof should be chosen around the structure and your goals — not around whatever the contractor prefers to install.
        </p>

        <h2>Frequently Asked Questions</h2>
        <div className="not-prose space-y-3 my-6">
          {FAQS.map((f) => (
            <details key={f.q} className="border border-border rounded-lg bg-card">
              <summary className="cursor-pointer font-semibold px-4 py-3">{f.q}</summary>
              <div className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
            </details>
          ))}
        </div>

        <h2 id="request-inspection">Schedule Your Free Roof Inspection</h2>
        <p>
          If you are not sure whether your roof needs a repair or a replacement, that is exactly what the free inspection is for. We walk the roof, take pictures, explain what we find, and give you a written estimate — no pressure.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 my-4">
          <Link to="/contact" className="btn-cta w-full sm:w-auto">
            <CalendarCheck className="size-5" /> Schedule My Free Inspection
          </Link>
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center justify-center gap-2 border-2 border-brand text-brand font-display font-bold uppercase tracking-wider rounded-md px-6 py-3 w-full sm:w-auto hover:bg-brand hover:text-brand-foreground transition-colors"
          >
            <Phone className="size-5" /> Call {BUSINESS.phone}
          </a>
        </div>
      </ServicePageBody>
    </>
  );
}
